import { useMemo } from 'react'
import { Badge } from '@/components/ui/shadcn/badge'
import { Button } from '@/components/ui/shadcn/button'
import { Calendar, Clock3, Edit, FileText, Instagram, Linkedin } from 'lucide-react'
import { type Campaign } from '@/pages/schedule-agent'
import { Link } from 'react-router'

type Props = {
  items: Campaign['posts']
}

export default function FigmaSlopContent({ items }: Props) {
  const sorted = useMemo(
    () =>
      [...items].sort(
        (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
      ),
    [items],
  )

  const groups = useMemo(() => {
    return sorted.reduce<Record<string, Campaign['posts']>>((acc, item) => {
      const d = new Date(item.scheduledAt)
      const key = d.toLocaleDateString()
      if (!acc[key]) acc[key] = []
      acc[key].push(item)
      return acc
    }, {})
  }, [sorted])

  // Get unique date keys in chronological order.
  // We avoid re-parsing locale strings with new Date() since they're not reliably parseable.
  const orderedDateKeys = useMemo(() => {
    const seen = new Set<string>()
    const result: { key: string; date: Date }[] = []
    for (const item of sorted) {
      const d = new Date(item.scheduledAt)
      const key = d.toLocaleDateString()
      if (!seen.has(key)) {
        seen.add(key)
        result.push({ key, date: d })
      }
    }
    return result
  }, [sorted])

  if (sorted.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-slate-200 bg-white">
        <div className="text-center text-slate-500">No generated content yet</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto rounded-lg border border-slate-200 bg-white p-3">
      <div className="space-y-6">
        {orderedDateKeys.map(({ key, date: dateObj }) => {
          const dayItems = groups[key]
          if (!dayItems) return null
          return (
            <div key={key}>
              <div className="mb-2 flex items-center gap-2 text-slate-700">
                <Calendar className="h-4 w-4" />
                <span className="text-sm font-semibold">{formatDateLabel(dateObj)}</span>
              </div>

              <div className="space-y-3">
                {dayItems.map((item, idx) => {
                  const d = new Date(item.scheduledAt)
                  const imageFiles = item.files.filter((f) => f.file.type.startsWith('image/'))
                  const otherFiles = item.files.filter((f) => !f.file.type.startsWith('image/'))

                  return (
                    <div
                      key={`${item.topic}-${idx}`}
                      className="overflow-hidden rounded-md border border-slate-200">
                      <div className="grid grid-cols-[auto_1fr] gap-3 bg-white p-3 md:grid-cols-[180px_1fr]">
                        {/* Left column: meta */}
                        <div className="flex flex-col gap-2 text-slate-700">
                          <div className="flex items-center gap-2">
                            <Clock3 className="h-4 w-4" />
                            <span className="text-sm">{formatTimeLabel(d)}</span>
                          </div>
                          <div>
                            {item.platform === 'instagram' ? (
                              <Badge variant="secondary" className="gap-1">
                                <Instagram className="h-3.5 w-3.5" /> Instagram
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="gap-1">
                                <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Right column: content */}
                        <div className="min-w-0">
                          <div className="mb-1 text-sm font-medium text-slate-900">
                            {item.topic}
                          </div>
                          <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                            {item.caption}
                          </div>

                          {/* Attachments */}
                          {(imageFiles.length > 0 || otherFiles.length > 0) && (
                            <div className="mt-3 space-y-2">
                              {imageFiles.length > 0 && (
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                                  {imageFiles.map((f) => {
                                    return (
                                      <div
                                        key={f.file.name}
                                        className="relative aspect-square overflow-hidden rounded border border-slate-200 bg-slate-50">
                                        <img
                                          src={f.url}
                                          crossOrigin="anonymous"
                                          alt={f.file.name}
                                          className="h-full w-full object-cover"
                                        />
                                      </div>
                                    )
                                  })}
                                </div>
                              )}

                              {otherFiles.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {otherFiles.map((f) => (
                                    <span
                                      key={f.file.name}
                                      className="inline-flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                                      <FileText className="h-3.5 w-3.5" />
                                      <span className="max-w-[200px] truncate">{f.file.name}</span>
                                      <span className="text-slate-400">
                                        • {formatFileSize(f.file.size)}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-3">
                            <Link to={`/workspace/${item.id}?campaignId=${item.campaignId}`}>
                              <Button size="sm" variant="outline" className="gap-2">
                                <Edit className="h-4 w-4" /> Edit
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatDateLabel(date: Date) {
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatTimeLabel(date: Date) {
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}
